import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import brandLogo from "@/assets/logo.png";
import {
  LayoutDashboard,
  Users,
  Contact as ContactIcon,
  Cpu,
  Tag,
  Radio,
  Webhook,
  MessageSquare,
  Code2,
  Network,
  Boxes,
  GitBranch,
  Settings2,
  LogOut,
  Activity,
  Sun,
  Moon,
  UserCog,
} from "lucide-react";
import { useAuth } from "@/store/auth";
import { useTheme } from "@/store/theme";
import { can } from "@/lib/rbac";
import { toast } from "sonner";

// Each nav entry declares the capability required to see it. Server-side
// authorisation still owns access — this only hides links the user cannot use.
const NAV = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", exact: true, cap: null },
  { to: "/clients", icon: Users, label: "Clients", cap: "read:clients" },
  { to: "/contacts", icon: ContactIcon, label: "Contacts", cap: "read:contacts" },
  { to: "/devices", icon: Cpu, label: "Devices", cap: "read:devices" },
  { to: "/device-types", icon: Tag, label: "Device types", cap: "read:device_types" },
  { to: "/gateways", icon: Radio, label: "Gateways", cap: "read:gateways" },
  { to: "/messages", icon: MessageSquare, label: "Messages", cap: "read:gw_messages" },
  { to: "/callbacks", icon: Webhook, label: "Callbacks", cap: "read:callbacks" },
  { to: "/envelope-rules", icon: Code2, label: "Envelope Rules", cap: "read:envelope" },
  { to: "/mqtt-topics", icon: Network, label: "MQTT Topics", cap: "read:mqtt" },
  { to: "/outputs", icon: Boxes, label: "Outputs", cap: "read:outputs" },
  { to: "/pipelines", icon: GitBranch, label: "Pipelines", cap: "read:pipelines" },
  { to: "/users", icon: UserCog, label: "Users", cap: "read:users" },
  { to: "/audit", icon: Activity, label: "Audit log", cap: "read:audit" },
  { to: "/system", icon: Settings2, label: "System", cap: null },
];

export default function Layout({ children }) {
  const logout = useAuth((s) => s.logout);
  const username = useAuth((s) => s.username);
  const role = useAuth((s) => s.role);
  const theme = useTheme((s) => s.theme);
  const toggle = useTheme((s) => s.toggle);
  const navigate = useNavigate();
  const isDark = theme === "dark";

  return (
    <div className="flex h-screen overflow-hidden bg-zinc-50 font-sans text-zinc-900">
      <aside
        className="w-60 shrink-0 border-r border-zinc-200 bg-zinc-50 flex flex-col"
        data-testid="app-sidebar"
      >
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-center">
          <img
            src={brandLogo}
            alt="IoT Admin Hub"
            className="h-20 w-auto object-contain dark:brightness-110"
            data-testid="brand-logo"
          />
        </div>

        <nav className="flex-1 overflow-y-auto scrollbar-thin py-3">
          {NAV.filter((item) => !item.cap || can(role, item.cap)).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.exact}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `mx-2 my-0.5 flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-indigo-800 text-white"
                    : "text-zinc-700 hover:bg-zinc-100"
                }`
              }
            >
              <item.icon className="w-4 h-4" strokeWidth={1.75} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-3 border-t border-zinc-200">
          <div className="px-3 py-2 mb-2 rounded-md bg-zinc-50 border border-zinc-200">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
              signed in
            </div>
            <div className="text-sm font-medium text-zinc-800 truncate" data-testid="sidebar-username">
              {username || "—"}
            </div>
            {role && (
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mt-0.5">
                {role}
              </div>
            )}
          </div>
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <button
              data-testid="sidebar-logout"
              onClick={() => {
                logout();
                toast.success("Signed out");
                navigate("/login");
              }}
              className="flex items-center justify-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm font-medium hover:bg-zinc-50 transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign out
            </button>
            <button
              data-testid="theme-toggle"
              onClick={toggle}
              title={isDark ? "Switch to light theme" : "Switch to dark theme"}
              aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
              className="flex items-center justify-center rounded-md border border-zinc-200 bg-white px-3 py-2 hover:bg-zinc-50 transition-colors"
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto scrollbar-thin">{children}</main>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-6">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight" data-testid="page-title">
          {title}
        </h1>
        {subtitle && (
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">{subtitle}</p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function PageContainer({ children }) {
  return <div className="p-6 md:p-8 max-w-[1600px] mx-auto">{children}</div>;
}
